import { Box, Button, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  httpMethodTone,
  primaryTint,
  variableNamespaceTone,
  warningSoftStyles,
} from 'components/@home/@settings/Tabs/Agents/agenticThemeHelpers';
import CloseIcon from 'mdi-react/CloseIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import AutoFixIcon from 'mdi-react/AutoFixIcon';
import { func, object } from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ToolPanelSection from 'components/@home/@settings/Tabs/Agents/toolPanels/ToolPanelSection';
import { normalizeToolTrigger } from 'constants/agenticToolTriggers';
import insertAtCaret from 'utils/insertAtCaret';
import Secret from 'services/api/Secret';
import { getSecretTypeSchema } from 'services/secrets/secretTypeSchemas';
import { I18n } from 'utils/i18n';

const MONO = 'ui-monospace, "SF Mono", Menlo, monospace';

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const METHODS_WITH_BODY = new Set(['POST', 'PUT', 'PATCH']);

const STANDARD_USER_TOKENS = [
  { key: 'user.fullName', descKey: 'UserTokenFullName' },
  { key: 'user.email', descKey: 'UserTokenEmail' },
  { key: 'user.employeeCode', descKey: 'UserTokenEmployeeCode' },
  { key: 'user.reportsTo', descKey: 'UserTokenReportsTo' },
  { key: 'user.jobTitle', descKey: 'UserTokenJobTitle' },
  { key: 'user.department', descKey: 'UserTokenDepartment' },
  { key: 'user.phone', descKey: 'UserTokenPhone' },
  { key: 'user.firstName', descKey: 'UserTokenFirstName' },
  { key: 'user.surName', descKey: 'UserTokenSurName' },
];

const STANDARD_CONTEXT_TOKENS = [
  { key: 'context.locale', descKey: 'ContextTokenLocale' },
  { key: 'context.timeZone', descKey: 'ContextTokenTimeZone' },
  { key: 'context.currentDateTime', descKey: 'ContextTokenCurrentDateTime' },
  { key: 'context.currentLocalDateTime', descKey: 'ContextTokenCurrentLocalDateTime' },
  { key: 'context.currentDate', descKey: 'ContextTokenCurrentDate' },
];

const AUTO_APPLIED_SECRET_TYPES = new Set([
  'bearer_token',
  'api_key',
  'basic_auth',
  'http_headers',
  'certificate',
]);

const useRequestPanelSx = () => {
  const theme = useTheme();
  return useMemo(
    () => ({
      layout: { display: 'flex', gap: 2.5, alignItems: 'flex-start' },
      main: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.5 },
      methodPicker: {
        display: 'inline-flex',
        gap: 0.5,
        p: 0.375,
        bgcolor: 'action.subtleLight',
        borderRadius: '6px',
        border: 1,
        borderColor: 'divider',
      },
      methodButton: {
        py: 0.75,
        px: 1.5,
        border: 'none',
        bgcolor: 'transparent',
        borderRadius: '4px',
        cursor: 'pointer',
        fontFamily: MONO,
        fontSize: theme.typography.pxToRem(12),
        fontWeight: 500,
        color: 'text.secondary',
        transition: 'all 0.15s',
        '&:hover': { color: 'text.primary' },
      },
      methodButtonActive: { fontWeight: 600 },
      headerRow: {
        display: 'grid',
        gridTemplateColumns: '200px 1fr 32px',
        gap: '1px',
        px: 0.5,
        fontSize: theme.typography.pxToRem(11),
        fontWeight: 600,
        color: 'text.secondary',
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
      },
      editorRow: {
        display: 'grid',
        gridTemplateColumns: '200px 1fr 32px',
        gap: '1px',
        alignItems: 'center',
      },
      monoField: {
        '& input, & textarea': {
          fontFamily: MONO,
          fontSize: theme.typography.pxToRem(12.5),
        },
      },
      emptyMapping: {
        p: 2.5,
        px: 2,
        border: 1,
        borderStyle: 'dashed',
        borderColor: 'divider',
        borderRadius: '6px',
        color: 'text.secondary',
        fontSize: theme.typography.pxToRem(12.5),
        textAlign: 'center',
        lineHeight: 1.5,
      },
      bodyEditor: {
        display: 'flex',
        border: 1,
        borderColor: 'divider',
        borderRadius: '6px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        '&:focus-within': {
          borderColor: 'primary.main',
          boxShadow: `0 0 0 3px ${primaryTint(theme, 0.12)}`,
        },
      },
      lineNumbers: {
        m: 0,
        p: 2,
        pr: 1.25,
        bgcolor: 'action.subtleLight',
        borderRight: 1,
        borderColor: 'divider',
        fontFamily: MONO,
        fontSize: theme.typography.pxToRem(12.5),
        lineHeight: 1.55,
        color: 'text.disabled',
        textAlign: 'right',
        userSelect: 'none',
        minWidth: 40,
        whiteSpace: 'pre',
      },
      bodyTextareaWrap: { position: 'relative', flex: 1, minWidth: 0 },
      bodyGenerateBtn: {
        position: 'absolute',
        top: theme.spacing(0.5),
        right: theme.spacing(0.5),
        zIndex: 1,
      },
      bodyTextarea: {
        '& textarea': {
          fontFamily: MONO,
          fontSize: theme.typography.pxToRem(12.5),
          lineHeight: 1.55,
          p: 0,
        },
        '& fieldset': { border: 'none' },
        '& .MuiInputBase-root': { p: 2, pr: 1.25, paddingRight: 5 },
      },
      fieldHint: {
        fontSize: theme.typography.pxToRem(12),
        color: 'text.secondary',
        lineHeight: 1.45,
        mb: 0.75,
      },
      fieldLabel: {
        fontSize: theme.typography.pxToRem(13),
        fontWeight: 500,
        color: 'text.primary',
        mb: 0.75,
      },
      variablesRail: {
        position: 'sticky',
        top: 0,
        alignSelf: 'flex-start',
        width: 280,
        flexShrink: 0,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: '8px',
        overflow: 'hidden',
        maxHeight: 'calc(100vh - 200px)',
        display: 'flex',
        flexDirection: 'column',
      },
      variablesHeader: {
        p: 1.75,
        px: 2,
        borderBottom: 1,
        borderColor: 'divider',
      },
      variablesTitle: {
        fontSize: theme.typography.pxToRem(14),
        fontWeight: 500,
        color: 'text.primary',
      },
      variablesSubtitle: {
        fontSize: theme.typography.pxToRem(11.5),
        color: 'text.secondary',
        mt: '2px',
        lineHeight: 1.45,
      },
      variablesScroll: { overflowY: 'auto', p: 1.5, px: 1 },
      namespaceLabel: {
        display: 'inline-flex',
        alignItems: 'center',
        fontFamily: MONO,
        fontSize: theme.typography.pxToRem(11),
        fontWeight: 600,
        py: '3px',
        px: '7px',
        borderRadius: '999px',
      },
      tokenButton: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '1px',
        py: 0.75,
        px: 1,
        bgcolor: 'transparent',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
        '&:hover': { bgcolor: primaryTint(theme, 0.06) },
      },
      tokenKey: {
        fontFamily: MONO,
        fontSize: theme.typography.pxToRem(12),
        fontWeight: 500,
        color: 'text.primary',
      },
      tokenDesc: {
        fontSize: theme.typography.pxToRem(11),
        color: 'text.secondary',
      },
      tokenEmpty: {
        mx: 1,
        p: 1,
        px: 1.25,
        bgcolor: 'action.subtleLight',
        border: 1,
        borderStyle: 'dashed',
        borderColor: 'divider',
        borderRadius: '6px',
        fontSize: theme.typography.pxToRem(11.5),
        color: 'text.secondary',
        lineHeight: 1.4,
      },
      secretGroupLabel: {
        display: 'block',
        py: 0.5,
        px: 1,
        pb: 0.25,
        fontFamily: MONO,
        fontSize: theme.typography.pxToRem(11),
        fontWeight: 600,
        color: 'text.secondary',
      },
      lifecycleHint: {
        mx: 1,
        mb: 1,
        p: 1,
        px: 1.25,
        borderRadius: '6px',
        fontSize: theme.typography.pxToRem(11.5),
        color: 'text.secondary',
        lineHeight: 1.4,
        ...warningSoftStyles(theme),
      },
    }),
    [theme],
  );
};

const formatArgDesc = param => {
  if (param.description?.trim()) return param.description.trim();
  const parts = [param.type || 'string'];
  if (param.required) parts.push(I18n.t('AgenticChatFlow.ParamRequired'));
  return parts.join(' · ');
};

const buildSecretFieldTokens = attachedSecrets => {
  const tokens = [];
  attachedSecrets.forEach(secret => {
    const schema = getSecretTypeSchema(secret.type);
    if (!schema?.fields?.length) return;
    schema.fields.forEach(field => {
      if (field.input === 'kv_list') return;
      const autoApplied = AUTO_APPLIED_SECRET_TYPES.has(secret.type);
      tokens.push({
        key: `secrets.${secret.name}.${field.key}`,
        desc: autoApplied
          ? I18n.t('AgenticChatFlow.VariablesSecretFieldAutoApplied', { field: field.key })
          : field.helper ||
            I18n.t('AgenticChatFlow.VariablesSecretsFieldDesc', { field: field.key }),
        group: secret.name,
      });
    });
  });
  return tokens;
};

const newClientKey = () => crypto.randomUUID();

const buildBodyTemplateFromParameters = parameters => {
  const args = (parameters || []).filter(p => p.name?.trim());
  if (!args.length) return '{\n}\n';
  const body = Object.fromEntries(args.map(p => [p.name.trim(), `{{args.${p.name.trim()}}}`]));
  return `${JSON.stringify(body, null, 2)}\n`;
};

const rowsToObject = (rows, { jsonPath = false } = {}) => {
  const out = {};
  rows.forEach(({ key, value }) => {
    if (!key?.trim()) return;
    let stored = value;
    if (jsonPath && stored && !stored.startsWith('$.')) {
      stored = `$.${stored.replace(/^\$\.?/, '')}`;
    }
    out[key.trim()] = stored;
  });
  return out;
};

const objectToRows = (obj, { stripJsonPathPrefix = false } = {}) =>
  Object.entries(obj || {}).map(([key, value]) => {
    let displayValue = value == null ? '' : String(value);
    if (stripJsonPathPrefix && displayValue.startsWith('$.')) {
      displayValue = displayValue.slice(2);
    }
    return {
      clientKey: newClientKey(),
      key,
      value: displayValue,
    };
  });

const MethodPicker = ({ value, onChange, sx }) => {
  const theme = useTheme();
  return (
    <Box sx={sx.methodPicker}>
      {METHODS.map(method => {
        const active = method === value;
        const tone = httpMethodTone(theme, method);
        return (
          <Box
            key={method}
            component="button"
            type="button"
            sx={[sx.methodButton, active && sx.methodButtonActive]}
            style={active ? { backgroundColor: tone.bg, color: tone.fg } : undefined}
            onClick={() => onChange(method)}
          >
            {method}
          </Box>
        );
      })}
    </Box>
  );
};

const BodyTemplateEditor = ({
  value,
  onChange,
  inputRef,
  insertHandlers,
  onGenerate,
  generateDisabled,
  sx,
}) => {
  const lineCount = Math.max((value || '').split('\n').length, 10);
  const lineNumbers = Array.from({ length: lineCount }, (_, i) => i + 1).join('\n');

  return (
    <Box sx={sx.bodyEditor}>
      <Box component="pre" sx={sx.lineNumbers} aria-hidden>
        {lineNumbers}
      </Box>
      <Box sx={sx.bodyTextareaWrap}>
        {onGenerate && (
          <Tooltip
            title={
              generateDisabled
                ? I18n.t('AgenticChatFlow.GenerateBodyFromArgsEmpty')
                : I18n.t('AgenticChatFlow.GenerateBodyFromArgs')
            }
          >
            <Box component="span" sx={sx.bodyGenerateBtn}>
              <IconButton
                size="small"
                onClick={onGenerate}
                disabled={generateDisabled}
                aria-label={I18n.t('AgenticChatFlow.GenerateBodyFromArgs')}
              >
                <AutoFixIcon size={18} />
              </IconButton>
            </Box>
          </Tooltip>
        )}
        <TextField
          inputRef={inputRef}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          multiline
          minRows={10}
          fullWidth
          sx={sx.bodyTextarea}
          placeholder={'{\n  "employeeCode": "{{args.employeeCode}}"\n}'}
          spellCheck={false}
          {...insertHandlers}
        />
      </Box>
    </Box>
  );
};

const VariablesRail = ({ parameters, attachedSecrets, trigger, onInsert, sx }) => {
  const theme = useTheme();
  const isLifecycleTrigger = normalizeToolTrigger(trigger) !== 'agent';

  const argTokens = useMemo(
    () =>
      (parameters || [])
        .filter(p => p.name && p.exposedToLlm !== false)
        .map(p => ({
          key: `args.${p.name}`,
          desc: formatArgDesc(p),
        })),
    [parameters],
  );

  const secretTokens = useMemo(
    () => buildSecretFieldTokens(attachedSecrets || []),
    [attachedSecrets],
  );

  const secretGroups = useMemo(() => {
    const groups = new Map();
    secretTokens.forEach(token => {
      const list = groups.get(token.group) || [];
      list.push(token);
      groups.set(token.group, list);
    });
    return [...groups.entries()];
  }, [secretTokens]);

  const namespaces = useMemo(
    () => [
      {
        id: 'args',
        label: 'args',
        descKey: 'VariablesArgsDesc',
        emptyKey: 'VariablesArgsEmpty',
        tokens: argTokens,
      },
      {
        id: 'user',
        label: 'user',
        descKey: 'VariablesUserDesc',
        tokens: STANDARD_USER_TOKENS.map(t => ({
          key: t.key,
          desc: I18n.t(`AgenticChatFlow.${t.descKey}`),
        })),
      },
      {
        id: 'context',
        label: 'context',
        descKey: 'VariablesContextDesc',
        footerKey: 'VariablesContextCustomHint',
        tokens: STANDARD_CONTEXT_TOKENS.map(t => ({
          key: t.key,
          desc: I18n.t(`AgenticChatFlow.${t.descKey}`),
        })),
      },
      {
        id: 'secrets',
        label: 'secrets',
        descKey: 'VariablesSecretsDesc',
        emptyKey: 'VariablesSecretsEmpty',
        tokens: secretTokens,
        grouped: secretGroups,
      },
    ],
    [argTokens, secretGroups, secretTokens],
  );

  const renderTokenButton = token => (
    <Box
      key={token.key}
      component="button"
      type="button"
      sx={sx.tokenButton}
      onMouseDown={e => e.preventDefault()}
      onClick={() => onInsert(token.key)}
    >
      <Box component="span" sx={sx.tokenKey}>{`{{${token.key}}}`}</Box>
      <Box component="span" sx={sx.tokenDesc}>
        {token.desc}
      </Box>
    </Box>
  );

  return (
    <Box component="aside" sx={sx.variablesRail}>
      <Box sx={sx.variablesHeader}>
        <Typography sx={sx.variablesTitle}>{I18n.t('AgenticChatFlow.RefVariables')}</Typography>
        <Typography sx={sx.variablesSubtitle}>
          {I18n.t('AgenticChatFlow.VariablesRailHint')}
        </Typography>
      </Box>
      <Box sx={sx.variablesScroll}>
        {isLifecycleTrigger && (
          <Box sx={sx.lifecycleHint}>{I18n.t('AgenticChatFlow.VariablesLifecycleTriggerHint')}</Box>
        )}
        {namespaces.map(ns => {
          const tone = variableNamespaceTone(theme, ns.id);
          return (
            <Box key={ns.id} sx={{ mb: 1.75 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '1px', px: 1, pb: 0.75 }}>
                <Box
                  component="span"
                  sx={sx.namespaceLabel}
                  style={{ backgroundColor: tone.bg, color: tone.fg }}
                >
                  {ns.label}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {I18n.t('AgenticChatFlow.VariablesTokenCount', { count: ns.tokens.length })}
                </Typography>
              </Box>
              {ns.tokens.length === 0 && ns.emptyKey ? (
                <Box sx={sx.tokenEmpty}>{I18n.t(`AgenticChatFlow.${ns.emptyKey}`)}</Box>
              ) : ns.grouped?.length ? (
                ns.grouped.map(([groupName, tokens]) => (
                  <Box key={groupName}>
                    <Box component="span" sx={sx.secretGroupLabel}>
                      {groupName}
                    </Box>
                    {tokens.map(renderTokenButton)}
                  </Box>
                ))
              ) : (
                ns.tokens.map(renderTokenButton)
              )}
              {ns.footerKey ? (
                <Box sx={[sx.tokenEmpty, { mt: 0.75 }]}>
                  {I18n.t(`AgenticChatFlow.${ns.footerKey}`)}
                </Box>
              ) : null}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const RequestPanel = ({ tool, setTool }) => {
  const sx = useRequestPanelSx();
  const http = useMemo(() => tool.http || {}, [tool.http]);
  const method = http.method || 'POST';
  const [allSecrets, setAllSecrets] = useState([]);
  const [headerRows, setHeaderRows] = useState(() => objectToRows(http.headersTemplate));
  const [mappingRows, setMappingRows] = useState(() =>
    objectToRows(http.outputMapping, { stripJsonPathPrefix: true }),
  );
  const insertTargetRef = useRef({ field: 'url', index: null });
  const bodyRef = useRef(null);
  const urlRef = useRef(null);
  const headerValueRefs = useRef({});

  const bindInsertField = (field, index = null) => ({
    onFocus: () => {
      insertTargetRef.current = { field, index };
    },
    inputProps: {
      onSelect: () => {
        insertTargetRef.current = { field, index };
      },
    },
  });

  const resolveInsertInput = () => {
    const active = document.activeElement;
    if (active === urlRef.current) {
      return { el: active, field: 'url', index: null };
    }
    if (active === bodyRef.current) {
      return { el: active, field: 'body', index: null };
    }
    const headerEntry = Object.entries(headerValueRefs.current).find(([, el]) => el === active);
    if (headerEntry) {
      return { el: active, field: 'headerValue', index: Number(headerEntry[0]) };
    }

    const { field, index } = insertTargetRef.current;
    if (field === 'url' && urlRef.current) {
      return { el: urlRef.current, field: 'url', index: null };
    }
    if (field === 'body' && bodyRef.current) {
      return { el: bodyRef.current, field: 'body', index: null };
    }
    if (field === 'headerValue' && headerValueRefs.current[index]) {
      return { el: headerValueRefs.current[index], field: 'headerValue', index };
    }
    return null;
  };

  useEffect(() => {
    Secret.list()
      .then(setAllSecrets)
      .catch(() => setAllSecrets([]));
  }, []);

  useEffect(() => {
    setHeaderRows(objectToRows(http.headersTemplate));
    setMappingRows(objectToRows(http.outputMapping, { stripJsonPathPrefix: true }));
  }, [tool._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const attachedSecrets = useMemo(
    () =>
      (tool.secretNames || []).map(name => allSecrets.find(s => s.name === name)).filter(Boolean),
    [allSecrets, tool.secretNames],
  );

  const patchHttp = useCallback(
    patch => {
      setTool({
        ...tool,
        http: { ...http, ...patch },
      });
    },
    [http, setTool, tool],
  );

  const syncHeaders = rows => {
    setHeaderRows(rows);
    patchHttp({ headersTemplate: rowsToObject(rows) });
  };

  const syncMapping = rows => {
    setMappingRows(rows);
    patchHttp({ outputMapping: rowsToObject(rows, { jsonPath: true }) });
  };

  const schemeInputs = useMemo(
    () => (tool.parameters || []).filter(p => p.name?.trim()),
    [tool.parameters],
  );

  const generateBodyFromParameters = () => {
    patchHttp({ bodyTemplate: buildBodyTemplateFromParameters(tool.parameters) });
  };

  const insertToken = async token => {
    const target = resolveInsertInput();
    if (!target?.el) return;

    const insert = `{{${token}}}`;
    const { el, field, index } = target;

    await insertAtCaret(el, insert, nextValue => {
      if (field === 'url') {
        patchHttp({ urlTemplate: nextValue });
      } else if (field === 'body') {
        patchHttp({ bodyTemplate: nextValue });
      } else if (field === 'headerValue') {
        const rows = headerRows.map((row, rowIndex) =>
          rowIndex === index ? { ...row, value: nextValue } : row,
        );
        syncHeaders(rows);
      }
    });
  };

  return (
    <Box sx={sx.layout}>
      <Box sx={sx.main}>
        <ToolPanelSection
          title={I18n.t('AgenticChatFlow.Endpoint')}
          description={I18n.t('AgenticChatFlow.EndpointDescription')}
        >
          <Stack spacing={2.5}>
            <Box>
              <Typography sx={sx.fieldLabel}>{I18n.t('AgenticChatFlow.UrlTemplate')}</Typography>
              <Typography sx={sx.fieldHint}>{I18n.t('AgenticChatFlow.UrlTemplateHint')}</Typography>
              <TextField
                inputRef={urlRef}
                value={http.urlTemplate || ''}
                onChange={e => patchHttp({ urlTemplate: e.target.value })}
                size="small"
                fullWidth
                sx={sx.monoField}
                placeholder="https://api.example.com/employees/{{args.employeeCode}}"
                {...bindInsertField('url')}
              />
            </Box>
            <Box>
              <Typography sx={sx.fieldLabel}>{I18n.t('AgenticChatFlow.Method')}</Typography>
              <MethodPicker value={method} onChange={next => patchHttp({ method: next })} sx={sx} />
            </Box>
            <Box>
              <Typography sx={sx.fieldLabel}>
                {I18n.t('AgenticChatFlow.Headers')}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    ml: 1,
                  }}
                >
                  {I18n.t('AgenticChatFlow.Optional')}
                </Typography>
              </Typography>
              <Typography sx={sx.fieldHint}>{I18n.t('AgenticChatFlow.HeadersHint')}</Typography>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {headerRows.map((row, index) => (
                  <Box key={row.clientKey} sx={sx.editorRow}>
                    <TextField
                      value={row.key}
                      onChange={e => {
                        const next = headerRows.map((r, i) =>
                          i === index ? { ...r, key: e.target.value } : r,
                        );
                        syncHeaders(next);
                      }}
                      size="small"
                      placeholder={I18n.t('AgenticChatFlow.HeaderNamePlaceholder')}
                      sx={sx.monoField}
                    />
                    <TextField
                      inputRef={el => {
                        if (el) headerValueRefs.current[index] = el;
                      }}
                      value={row.value}
                      onChange={e => {
                        const next = headerRows.map((r, i) =>
                          i === index ? { ...r, value: e.target.value } : r,
                        );
                        syncHeaders(next);
                      }}
                      size="small"
                      placeholder={I18n.t('AgenticChatFlow.HeaderValuePlaceholder')}
                      sx={sx.monoField}
                      {...bindInsertField('headerValue', index)}
                    />
                    <IconButton
                      size="small"
                      aria-label={I18n.t('AgenticChatFlow.Remove')}
                      onClick={() => syncHeaders(headerRows.filter((_, i) => i !== index))}
                    >
                      <CloseIcon size={16} />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<PlusIcon size={16} />}
                  onClick={() =>
                    syncHeaders([...headerRows, { clientKey: newClientKey(), key: '', value: '' }])
                  }
                  sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                >
                  {I18n.t('AgenticChatFlow.AddHeader')}
                </Button>
              </Stack>
            </Box>
          </Stack>
        </ToolPanelSection>

        {METHODS_WITH_BODY.has(method) && (
          <ToolPanelSection
            title={I18n.t('AgenticChatFlow.BodyTemplate')}
            description={I18n.t('AgenticChatFlow.BodyTemplateDescription')}
          >
            <BodyTemplateEditor
              value={http.bodyTemplate}
              onChange={v => patchHttp({ bodyTemplate: v })}
              inputRef={bodyRef}
              insertHandlers={bindInsertField('body')}
              onGenerate={generateBodyFromParameters}
              generateDisabled={!schemeInputs.length}
              sx={sx}
            />
            <Typography sx={[sx.fieldHint, { mt: 1.5 }]}>
              {I18n.t('AgenticChatFlow.BodyTemplateHelp')}
            </Typography>
          </ToolPanelSection>
        )}

        <ToolPanelSection
          title={I18n.t('AgenticChatFlow.OutputMapping')}
          description={I18n.t('AgenticChatFlow.OutputMappingDescription')}
        >
          <Box sx={sx.headerRow}>
            <span>{I18n.t('AgenticChatFlow.OutputKey')}</span>
            <span>{I18n.t('AgenticChatFlow.JsonPath')}</span>
            <span />
          </Box>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {mappingRows.length === 0 ? (
              <Box sx={sx.emptyMapping}>{I18n.t('AgenticChatFlow.OutputMappingEmpty')}</Box>
            ) : (
              mappingRows.map((row, index) => (
                <Box key={row.clientKey} sx={sx.editorRow}>
                  <TextField
                    value={row.key}
                    onChange={e => {
                      const next = mappingRows.map((r, i) =>
                        i === index ? { ...r, key: e.target.value } : r,
                      );
                      syncMapping(next);
                    }}
                    size="small"
                    placeholder="requestId"
                    sx={sx.monoField}
                  />
                  <TextField
                    value={row.value}
                    onChange={e => {
                      const next = mappingRows.map((r, i) =>
                        i === index ? { ...r, value: e.target.value } : r,
                      );
                      syncMapping(next);
                    }}
                    size="small"
                    placeholder="data.id"
                    sx={sx.monoField}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <Typography
                            component="span"
                            sx={{
                              color: 'text.secondary',
                              fontFamily: MONO,
                              mr: 0.5,
                            }}
                          >
                            $
                          </Typography>
                        ),
                      },
                    }}
                  />
                  <IconButton
                    size="small"
                    aria-label={I18n.t('AgenticChatFlow.Remove')}
                    onClick={() => syncMapping(mappingRows.filter((_, i) => i !== index))}
                  >
                    <CloseIcon size={16} />
                  </IconButton>
                </Box>
              ))
            )}
            <Button
              size="small"
              startIcon={<PlusIcon size={16} />}
              onClick={() =>
                syncMapping([...mappingRows, { clientKey: newClientKey(), key: '', value: '' }])
              }
              sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
            >
              {I18n.t('AgenticChatFlow.AddMapping')}
            </Button>
          </Stack>
        </ToolPanelSection>
      </Box>

      <VariablesRail
        parameters={tool.parameters}
        attachedSecrets={attachedSecrets}
        trigger={tool.trigger}
        onInsert={insertToken}
        sx={sx}
      />
    </Box>
  );
};

RequestPanel.propTypes = {
  tool: object.isRequired,
  setTool: func.isRequired,
};

export default RequestPanel;
